'use client'

import { memo } from 'react'
import { motion } from 'framer-motion'
import {
  HardDrive,
  KeyRound,
  Mic,
  ShieldCheck,
  WifiOff,
  Fingerprint,
} from 'lucide-react'
import { EvervaultCard, EvervaultCornerIcon } from '@/components/ui/evervault-card'
import { NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'

const STORAGE_ROWS = [
  {
    icon: Mic,
    title: 'Audio & Whisper',
    body: 'Capture and STT run on-device. Raw Dictate never uploads audio or transcript to Google.',
  },
  {
    icon: HardDrive,
    title: 'Models in app data',
    body: 'Whisper and Gemma land only after you click Download: pinned revision, SHA-256, atomic install under NINK app data.',
  },
  {
    icon: KeyRound,
    title: 'Keys in the OS vault',
    body: 'Optional Google BYOK uses Keychain on macOS and Credential Manager on Windows, not plaintext prefs.',
  },
  {
    icon: Fingerprint,
    title: 'No silent fallback',
    body: 'Local Gemma never auto-switches to cloud. Cloud rewrite runs only when you explicitly select Google AI.',
  },
] as const

/** baby: You → NINK on device → cursor; cloud dashed opt-in */
const LocalFlowDiagram = memo(function LocalFlowDiagram() {
  return (
    <div className="ninxl-pixel-border ninxl-theme-bg-surface mx-auto mb-6 max-w-md p-4">
      <svg viewBox="0 0 340 110" className="h-auto w-full" aria-hidden>
        <rect
          x="6"
          y="38"
          width="70"
          height="34"
          className="fill-[color:var(--ninxl-theme-bg-frame)] stroke-[color:var(--ninxl-theme-border-soft)]"
          strokeWidth="1.5"
        />
        <text
          x="41"
          y="59"
          textAnchor="middle"
          className="fill-[color:var(--ninxl-theme-fg-muted)] text-[10px] font-semibold"
        >
          You
        </text>

        <rect
          x="118"
          y="28"
          width="104"
          height="54"
          className="fill-[color-mix(in_srgb,var(--ninxl-brand-teal)_12%,transparent)] stroke-[color:var(--ninxl-brand-teal)]"
          strokeWidth="1.5"
        />
        <text
          x="170"
          y="52"
          textAnchor="middle"
          className="fill-[color:var(--ninxl-theme-fg)] text-[11px] font-bold"
        >
          NINK
        </text>
        <text
          x="170"
          y="68"
          textAnchor="middle"
          className="fill-[color:var(--ninxl-brand-teal)] text-[8px]"
        >
          on your machine
        </text>

        <rect
          x="264"
          y="38"
          width="70"
          height="34"
          className="fill-[color:var(--ninxl-theme-bg-frame)] stroke-[color:var(--ninxl-theme-border-soft)]"
          strokeWidth="1.5"
          strokeDasharray="4 3"
        />
        <text
          x="299"
          y="54"
          textAnchor="middle"
          className="fill-[color:var(--ninxl-theme-fg-muted)] text-[9px]"
        >
          Cloud
        </text>
        <text
          x="299"
          y="66"
          textAnchor="middle"
          className="fill-[color:var(--ninxl-theme-fg-muted)] text-[7px]"
        >
          BYOK only
        </text>

        <line
          x1="76"
          y1="55"
          x2="118"
          y2="55"
          className="ninxl-data-flow-line stroke-[color:var(--ninxl-brand-teal)]"
          strokeWidth="2"
        />
        <line
          x1="222"
          y1="55"
          x2="264"
          y2="55"
          className="stroke-[color:var(--ninxl-theme-border-soft)]"
          strokeWidth="1.5"
          strokeDasharray="5 4"
        />
      </svg>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-4">
        <span className="ninxl-8bit-label inline-flex items-center gap-1.5 opacity-80">
          <WifiOff className="h-3 w-3" aria-hidden /> Offline Dictate
        </span>
        <span className="ninxl-8bit-label inline-flex items-center gap-1.5 opacity-80">
          <ShieldCheck className="h-3 w-3" aria-hidden /> Local default
        </span>
      </div>
    </div>
  )
})

/** Cipher vault: hover reveals cryptographic field for NINK privacy */
const NinkCipherVault = memo(function NinkCipherVault() {
  return (
    <div className="ninxl-pixel-border ninxl-theme-bg-frame relative mx-auto h-[28rem] w-full max-w-sm overflow-hidden md:h-[30rem]">
      <EvervaultCornerIcon className="absolute -left-3 -top-3 z-20 h-6 w-6 text-[color:var(--ninxl-theme-fg)]" />
      <EvervaultCornerIcon className="absolute -bottom-3 -left-3 z-20 h-6 w-6 text-[color:var(--ninxl-theme-fg)]" />
      <EvervaultCornerIcon className="absolute -right-3 -top-3 z-20 h-6 w-6 text-[color:var(--ninxl-theme-fg)]" />
      <EvervaultCornerIcon className="absolute -bottom-3 -right-3 z-20 h-6 w-6 text-[color:var(--ninxl-theme-fg)]" />

      <EvervaultCard
        text="NINK"
        size="lg"
        className="absolute inset-0 h-full w-full"
        roundedClassName="rounded-none"
      >
        <h2 className="text-sm font-light">
          Hover to scrub the cipher. Dictate audio, Whisper, and local rewrite stay on this machine
          unless you opt into BYOK.
        </h2>
        <p className="mt-3 inline-block border border-[color:var(--ninxl-theme-border-soft)] px-2 py-0.5 text-xs font-light">
          Keychain · App data · No silent cloud
        </p>
      </EvervaultCard>
    </div>
  )
})

/**
 * baby: Privacy + local storage story with cipher animation. NINK-specific, not NINX compliance.
 */
export const NinkPrivacySection = memo(function NinkPrivacySection() {
  return (
    <div className="mx-auto max-w-6xl px-6">
      <NinxlScrollReveal className="mb-10 text-center">
        <div className="nink-hybrid-eyebrow mb-3 justify-center">
          Security · local storage
        </div>
        <h2 className="nink-hybrid-display ninxl-theme-text mb-4 text-2xl md:text-4xl">
          Encrypted at the edge. Stored on your device.
        </h2>
        <p className="nink-hybrid-body mx-auto max-w-2xl text-sm md:text-base">
          NINK is built so voice writing does not need a server. Models, history, and credentials live
          under your control: cloud is an explicit choice, never a silent path.
        </p>
      </NinxlScrollReveal>

      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
        <NinxlScrollReveal variant="left">
          <LocalFlowDiagram />

          <div className="space-y-3">
            {STORAGE_ROWS.map((row, index) => (
              <motion.div
                key={row.title}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ delay: 0.08 + index * 0.07, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="nink-hybrid-card p-4 text-left md:p-5">
                  <div className="flex items-start gap-3">
                    <span className="nink-hybrid-chip mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center !rounded-xl !px-0">
                      <row.icon className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <div>
                      <h3 className="nink-hybrid-body ninxl-theme-text mb-1 text-sm font-semibold md:text-base">
                        {row.title}
                      </h3>
                      <p className="nink-hybrid-body text-sm md:text-base">{row.body}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </NinxlScrollReveal>

        <NinxlScrollReveal variant="right" delay={0.1}>
          <NinkCipherVault />
        </NinxlScrollReveal>
      </div>
    </div>
  )
})
